import {
  HEALTH_CARD_PHOTO_LIMITS,
  HEALTH_CARD_PHOTO_MEDIA_TYPE,
  inspectHealthCardJpeg,
  stripJpegMetadata,
} from "@emr/contracts";

/**
 * A health card photo ready to send (PA-13): the patient's picture redrawn on a canvas at most
 * clientLongEdgePx on its long edge, re-encoded as JPEG, its metadata (Exif, GPS, comments)
 * dropped by stripJpegMetadata and checked with inspectHealthCardJpeg, as the API checks it
 * again. Held in memory only, for the session.
 */
export interface PreparedHealthCardPhoto {
  /** The bytes to send. */
  readonly jpeg: Uint8Array;
  readonly width: number;
  readonly height: number;
  /**
   * The redrawn picture for the on-page preview. The page draws it on a canvas: the
   * Content-Security-Policy allows no blob: images.
   */
  readonly preview: ImageBitmap;
}

/** The picture could not be decoded, or did not make a photo the clinic accepts. */
export class HealthCardPhotoUnreadableError extends Error {
  public constructor() {
    super("The photo could not be used");
    this.name = "HealthCardPhotoUnreadableError";
  }
}

/**
 * Whether this browser can prepare a photo: decode it (createImageBitmap) and re-encode it
 * (canvas toBlob). Without both the photo row is not shown and the patient types the details.
 */
export function healthCardPhotoSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.createImageBitmap === "function" &&
    typeof HTMLCanvasElement !== "undefined" &&
    typeof HTMLCanvasElement.prototype.toBlob === "function" &&
    typeof Blob.prototype.arrayBuffer === "function"
  );
}

/** The picture's size once its long edge is at most longEdge (never enlarged). */
export function scaledSize(
  width: number,
  height: number,
  longEdge: number = HEALTH_CARD_PHOTO_LIMITS.clientLongEdgePx,
): { readonly width: number; readonly height: number } {
  const scale = Math.min(1, longEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Decodes the file the way the camera held it (Exif orientation applied, where supported). */
async function decode(file: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (caught) {
    // Older browsers know only "none" and "flipY" and refuse the option itself.
    if (caught instanceof TypeError) return createImageBitmap(file);
    throw caught;
  }
}

function jpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob(
      resolve,
      HEALTH_CARD_PHOTO_MEDIA_TYPE,
      HEALTH_CARD_PHOTO_LIMITS.clientJpegQuality,
    );
  });
}

/**
 * Prepares the chosen or taken picture: decode, redraw at most clientLongEdgePx on its long edge
 * on a white canvas, re-encode as JPEG at clientJpegQuality, drop the metadata and check the
 * result. Throws HealthCardPhotoUnreadableError for anything that does not make an accepted
 * photo (not a picture, or too small).
 */
export async function prepareHealthCardPhoto(file: Blob): Promise<PreparedHealthCardPhoto> {
  let source: ImageBitmap;
  try {
    source = await decode(file);
  } catch {
    throw new HealthCardPhotoUnreadableError();
  }
  const canvas = document.createElement("canvas");
  try {
    const size = scaledSize(source.width, source.height);
    canvas.width = size.width;
    canvas.height = size.height;
    const context = canvas.getContext("2d");
    if (context === null) throw new HealthCardPhotoUnreadableError();
    // JPEG has no transparency: a transparent picture would otherwise turn black.
    context.fillStyle = "#fff";
    context.fillRect(0, 0, size.width, size.height);
    context.imageSmoothingQuality = "high";
    context.drawImage(source, 0, 0, size.width, size.height);
    const blob = await jpegBlob(canvas);
    if (blob === null || blob.type !== HEALTH_CARD_PHOTO_MEDIA_TYPE) {
      throw new HealthCardPhotoUnreadableError();
    }
    const jpeg = stripJpegMetadata(new Uint8Array(await blob.arrayBuffer()));
    const inspection = jpeg === null ? null : inspectHealthCardJpeg(jpeg);
    if (jpeg === null || inspection === null || !inspection.ok) {
      throw new HealthCardPhotoUnreadableError();
    }
    const preview = await createImageBitmap(canvas);
    return { jpeg, width: inspection.width, height: inspection.height, preview };
  } catch (caught) {
    if (caught instanceof HealthCardPhotoUnreadableError) throw caught;
    throw new HealthCardPhotoUnreadableError();
  } finally {
    source.close();
    // Frees the canvas's pixels now rather than at garbage collection.
    canvas.width = 0;
    canvas.height = 0;
  }
}
